import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Calendar, ExternalLink, Newspaper, Award, Mic, Star, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT_HEADING = "'Cormorant Garamond', Cambria, Georgia, serif";
const FONT_BODY = "'Jost', sans-serif";

const categoryIcons = { news: Newspaper, press_release: FileText, interview: Mic, award: Award, feature: Star };
const categoryLabels = { news: 'News', press_release: 'Press Release', interview: 'Interview', award: 'Award', feature: 'Feature' };

export default function NewsroomPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await fetch(`${API}/media`);
        if (res.ok) setArticles(await res.json());
      } catch {}
      setLoading(false);
    };
    fetchArticles();
  }, []);

  const categories = ['all', ...new Set(articles.map(a => a.category))];
  const filtered = filter === 'all' ? articles : articles.filter(a => a.category === filter);

  return (
    <div className="min-h-screen bg-[#1A1A1A]" data-testid="newsroom-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 md:px-12 py-16 pt-28">
        <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>Media & Press</p>
        <h1 className="text-4xl md:text-5xl text-[#FAF8F5] mb-6 tracking-tight" style={{ fontFamily: FONT_HEADING }}>Newsroom</h1>
        <p className="text-[#999] text-lg mb-12 max-w-2xl" style={{ fontFamily: FONT_BODY }}>
          Latest news coverage, press releases, and media mentions of Sàwai Capital.
        </p>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              data-testid={`media-filter-${cat}`}
              className={`px-5 py-2 text-xs uppercase tracking-wider transition-colors ${
                filter === cat ? 'bg-[#A0743A] text-[#FAF8F5]' : 'border border-[#A0743A]/30 text-[#A0743A] hover:bg-[#A0743A]/10'
              }`}
              style={{ fontFamily: FONT_BODY }}
            >
              {cat === 'all' ? 'All' : categoryLabels[cat] || cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-[#A0743A]/10">
            <Newspaper size={32} className="text-[#A0743A] mx-auto mb-4" strokeWidth={1.25} />
            <p className="text-[#999] text-lg" style={{ fontFamily: FONT_BODY }}>No media coverage yet. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="media-articles-grid">
            {filtered.map(article => {
              const Icon = categoryIcons[article.category] || Newspaper;
              return (
                <article
                  key={article.article_id}
                  className="border border-[#A0743A]/15 hover:border-[#A0743A]/40 transition-all group"
                  style={{ transition: 'transform 0.3s ease, border-color 0.3s ease' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                  data-testid={`media-article-${article.article_id}`}
                >
                  {article.cover_image && (
                    <div className="overflow-hidden h-48">
                      <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="flex items-center gap-1.5 text-[#A0743A] text-[10px] uppercase tracking-wider font-semibold" style={{ fontFamily: FONT_BODY }}>
                        <Icon size={12} /> {categoryLabels[article.category] || article.category}
                      </span>
                      {article.published_date && (
                        <span className="flex items-center gap-1 text-[#555] text-[10px]" style={{ fontFamily: FONT_BODY }}>
                          <Calendar size={10} /> {new Date(article.published_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>

                    <h3 className="text-lg text-[#FAF8F5] mb-2 group-hover:text-[#A0743A] transition-colors leading-snug" style={{ fontFamily: FONT_HEADING }}>{article.title}</h3>

                    {article.source && (
                      <p className="text-[#C9A96E] text-xs uppercase tracking-wider font-semibold mb-3" style={{ fontFamily: FONT_BODY }}>{article.source}</p>
                    )}

                    {article.summary && (
                      <p className="text-[#999] text-sm leading-relaxed mb-4 line-clamp-3" style={{ fontFamily: FONT_BODY }}>{article.summary}</p>
                    )}

                    {article.source_url && (
                      <a
                        href={article.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-[#A0743A] text-xs uppercase tracking-wider font-semibold hover:text-[#C9A96E] transition-colors"
                        style={{ fontFamily: FONT_BODY }}
                        data-testid={`read-article-${article.article_id}`}
                      >
                        Read Full Article <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
